// The console's translation layer (D63): ICU MessageFormat catalogs in messages/<locale>.json,
// the same format and library as @apsio/i18n and the site. The language follows the signed-in
// user's profile, and the browser's before sign-in; dates and numbers follow the language. Times
// follow the profile's time zone (APS-398), else the browser's; a call that names its own zone
// (a day bucket's `timeZone: 'UTC'`) keeps it.
// Telemetry text, code and identifiers are never translated.
import { type Locale, locales, negotiateLocale } from '@apsio/i18n'
import { type UiText, UiTextProvider } from '@apsio/ui'
import { IntlMessageFormat } from 'intl-messageformat'
import { createContext, Fragment, type ReactNode, useContext, useMemo } from 'react'
import en from '../../messages/en.json'
import ptBR from '../../messages/pt-BR.json'

export type MessageId = keyof typeof en
export type Values = Record<string, string | number | Date>
/** Values for `rich`: a node can stand in a message too (an identifier set as code). */
export type RichValues = Record<string, string | number | Date | ReactNode>
export type { Locale }
export { locales }

const catalogs: Record<Locale, Record<MessageId, string>> = { en, 'pt-BR': ptBR }

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (locales as readonly string[]).includes(value)
}

/** The browser's language, before the profile is known. */
export function browserLocale(): Locale {
  return negotiateLocale(typeof navigator === 'undefined' ? undefined : navigator.languages)
}

export interface Translator {
  locale: Locale
  /** The person's IANA time zone, or undefined for the browser's own. */
  timeZone: string | undefined
  t: (id: MessageId, values?: Values) => string
  number: (value: number, options?: Intl.NumberFormatOptions) => string
  /** A rate (0..1) as a percentage with two decimals, as crash-free rates are read. */
  percent: (rate: number, digits?: number) => string
  date: (value: Date | string | number, options?: Intl.DateTimeFormatOptions) => string
  /** "6 min ago", "in 3 days"; compact, a past time without its "ago": "6 min" (APS-446). */
  relative: (value: Date | string, now?: Date, style?: 'long' | 'compact') => string
  /** A past time as a figure and its small word, as a strip cell sets it: ["4 min", "ago"]. */
  relativeParts: (value: Date | string, now?: Date) => [string, string]
  /**
   * A message whose identifiers and commands the catalog marks `<code>…</code>`, set in mono
   * (APS-385). `t` reads the same message as plain text, for a label or a title.
   */
  rich: (id: MessageId, values?: RichValues) => ReactNode
}

/** A time zone the browser can format in, else undefined (the browser's own). */
export function knownTimeZone(zone: string | null | undefined): string | undefined {
  if (!zone) return undefined
  try {
    new Intl.DateTimeFormat('en', { timeZone: zone })
    return zone
  } catch {
    return undefined
  }
}

export function createTranslator(locale: Locale, zone?: string | null): Translator {
  const timeZone = knownTimeZone(zone)
  const cache = new Map<MessageId, IntlMessageFormat>()
  const numbers = new Map<string, Intl.NumberFormat>()
  const nf = (options: Intl.NumberFormatOptions = {}) => {
    const k = JSON.stringify(options)
    let f = numbers.get(k)
    if (!f) {
      f = new Intl.NumberFormat(locale, options)
      numbers.set(k, f)
    }
    return f
  }
  const formatOf = (id: MessageId) => {
    let format = cache.get(id)
    if (!format) {
      format = new IntlMessageFormat(catalogs[locale][id] ?? catalogs.en[id] ?? id, locale)
      cache.set(id, format)
    }
    return format
  }
  // A `<code>` tag reads as its text here: `t` is for plain strings (labels, titles, aria).
  const plain = (chunks: unknown[]) => chunks.join('')
  const t: Translator['t'] = (id, values) => {
    const out = formatOf(id).format({ code: plain, ...values })
    return Array.isArray(out) ? out.join('') : String(out)
  }
  const code = (chunks: ReactNode[]) => <code className="i-code">{chunks}</code>
  const rich: Translator['rich'] = (id, values) => {
    const out = formatOf(id).format<ReactNode>({ ...values, code })
    return Array.isArray(out)
      ? out.map((part, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: a message's parts keep their order
          <Fragment key={i}>{part}</Fragment>
        ))
      : out
  }
  return {
    locale,
    timeZone,
    t,
    rich,
    number: (value, options) => nf(options).format(value),
    percent: (rate, digits = 2) =>
      nf({ style: 'percent', minimumFractionDigits: digits, maximumFractionDigits: digits }).format(
        rate,
      ),
    // Times read on a 24-hour clock in every locale, as the design writes them (09:12).
    // The person's zone unless the call names one (day buckets are UTC's).
    date: (value, options = { month: 'short', day: 'numeric' }) => {
      const zoned = timeZone && options.timeZone === undefined ? { ...options, timeZone } : options
      return new Intl.DateTimeFormat(
        locale,
        (zoned.hour !== undefined || zoned.timeStyle !== undefined) &&
          zoned.hourCycle === undefined &&
          zoned.hour12 === undefined
          ? { ...zoned, hourCycle: 'h23' }
          : zoned,
      ).format(new Date(value))
    },
    // "just now", "4 min ago", "1 h ago", "yesterday", "12 d ago"; pt-BR "há 4 min" (APS-339). Intl's short
    // and narrow styles write "4 min." and "4m", so the console writes its own.
    // Compact drops "ago" (pt-BR "há"), "4 min", where the heading already says the times are
    // recent: Home's agent feed under "last 15 min" (Mosaic, APS-446).
    relative(value, now = new Date(), style = 'long') {
      return relativeOf(value, now, style)
    },
    relativeParts(value, now = new Date()) {
      const seconds = (new Date(value).getTime() - now.getTime()) / 1000
      const [n, unit] = unitOf(Math.abs(seconds))
      // "just now", "yesterday" and future times stay whole.
      if (seconds >= 0 || unit === 's' || (unit === 'd' && n === 1))
        return [relativeOf(value, now), '']
      return [t('time.agoFigure', { n: nf().format(n), unit }), t('time.agoWord')]
    },
  }
  function unitOf(abs: number): [number, string] {
    // The unit is chosen after rounding, so 59.7 minutes reads "1 h", never "60 min".
    return Math.round(abs) < 60
      ? [Math.round(abs), 's']
      : Math.round(abs / 60) < 60
        ? [Math.round(abs / 60), 'min']
        : Math.round(abs / 3600) < 24
          ? [Math.round(abs / 3600), 'h']
          : [Math.round(abs / 86_400), 'd']
  }
  function relativeOf(value: Date | string, now: Date, style: 'long' | 'compact' = 'long'): string {
    const seconds = (new Date(value).getTime() - now.getTime()) / 1000
    const [n, unit] = unitOf(Math.abs(seconds))
    const past = seconds < 0
    // As the mock: "just now" under a minute ago, "yesterday" for one day ago.
    if (past && unit === 's') return t('time.justNow')
    if (past && unit === 'd' && n === 1) return t('time.yesterday')
    if (n === 0) return t('time.now')
    return t(past ? (style === 'compact' ? 'time.since' : 'time.ago') : 'time.in', {
      n: nf().format(n),
      unit,
    })
  }
}

const I18nContext = createContext<Translator>(createTranslator('en'))

/** Console D's own words (@apsio/ui's components), from this catalogue. */
function uiText(t: Translator['t']): UiText {
  return {
    status: {
      new: t('d.status.new'),
      reg: t('d.status.regressed'),
      on: t('d.status.ongoing'),
      res: t('d.status.resolved'),
      fire: t('d.status.firing'),
      miss: t('d.status.missing'),
      mute: t('d.status.muted'),
      ok: t('d.status.ok'),
    },
    copy: t('d.code.copy'),
    copied: t('d.code.copied'),
    copyFailed: t('d.code.copyFailed'),
    chart: {
      table: t('d.chart.table'),
      hideTable: t('d.chart.hideTable'),
      resetZoom: t('d.chart.resetZoom'),
      only: t('d.chart.only'),
      keys: ({ open, zoom }) =>
        [
          t('d.chart.keys'),
          ...(open ? [t('d.chart.keysOpen')] : []),
          ...(zoom ? [t('d.chart.keysZoom')] : []),
        ].join(' '),
      none: t('d.chart.none'),
      roleName: t('d.chart.role'),
      value: t('d.chart.value'),
      x: t('d.chart.x'),
      zoomed: (from, to) => t('d.chart.zoomed', { from, to }),
    },
  }
}

export function I18nProvider({
  locale,
  timeZone,
  children,
}: {
  locale: Locale
  /** The profile's time zone (APS-398); undefined or null: the browser's. */
  timeZone?: string | null | undefined
  children: ReactNode
}) {
  const translator = useMemo(() => createTranslator(locale, timeZone), [locale, timeZone])
  const text = useMemo(() => uiText(translator.t), [translator])
  return (
    <I18nContext.Provider value={translator}>
      <UiTextProvider value={text}>{children}</UiTextProvider>
    </I18nContext.Provider>
  )
}

export const useI18n = (): Translator => useContext(I18nContext)
